import {instanceGusaBackend} from 'libs/api';

import {API_URL} from 'constants/api';
import {ENDPOINTS} from 'constants/endpoints';
import {CATEGORY, PDP} from '@optimaxdev/analytics/mobile';
import {sendAnalytics} from 'libs/analytics/sendAnalytics';
import {AxiosErrorCustom} from '@typescript/common/authorizationTypes';

/**
 * Extracts error message from token response
 * @param {AxiosErrorCustom<{message: string}>} error - error response
 * @return {string} error message
 */
export const parseMessageFromError = (error: AxiosErrorCustom<{message: string}>): string =>
    error.response ? error.response.data.message || '' : '';

export const wishlist = {
    /**
     * Fetch user wishlist items
     * @returns {Promise<[]>} promise with request to get user wishlist
     */
    get: async (): Promise<[null | string, number[]]> => {
        try {
            const {data} = await instanceGusaBackend({
                method: 'get',
                baseURL: API_URL,
                url: `${ENDPOINTS.WISHLIST}`,
            });

            return [null, data.map(item => item.productId)];
        } catch (error) {
            return [parseMessageFromError(error), []];
        }
    },
    /**
     * Add several wishlist items by their ids
     * @param {number[]} ids - wishlist items ids
     * @return {Promise<[]>} Response from backend
     */
    addMultiple: async (ids: number[]): Promise<[null | string]> => {
        try {
            await instanceGusaBackend({
                method: 'post',
                baseURL: API_URL,
                url: `${ENDPOINTS.WISHLIST}`,
                data: ids,
            });

            return [null];
        } catch (error) {
            return [parseMessageFromError(error)];
        }
    },
    /**
     * Add wishlist item by id
     * @param {number} id - wishlist item id
     * @return {Promise<[]>} Response from backend
     */
    add: async (id: number): Promise<[null | string]> => wishlist.addMultiple([id]),
    /**
     * Remove wishlist item by id
     * @param {number} id - wishlist item id
     * @return {Promise<[]>} Response from backend
     */
    remove: async (id: number): Promise<[null | string]> => {
        try {
            await instanceGusaBackend({
                method: 'delete',
                baseURL: API_URL,
                url: `${ENDPOINTS.WISHLIST}/${id}`,
            });

            return [null];
        } catch (error) {
            return [parseMessageFromError(error)];
        }
    },
};

/**
 * Report analytics about a change in the wishlist
 * @param {boolean} isProductPage - will return true if the product is the current page
 * @param {boolean} isAdded - new product is added
 * @param {boolean} isOver - exceeding the limit of added products
 */
export const reportChangeWishlist = (isProductPage = false, isAdded = true, isOver = false) => {
    const mainLabel = isAdded ? 'Add to Wishlist' : 'Remove from Wishlist';

    if (isProductPage) {
        sendAnalytics(
            PDP.WISHLIST_USAGE,
            `${mainLabel}${isAdded && isOver ? ' - Guest limit popup' : ''}`,
        );
    } else {
        sendAnalytics(
            CATEGORY.PRODUCT,
            `${mainLabel}${isAdded && isOver ? ' - Guest limit popup' : ''}`,
        );
    }
};
